import type { DetectedShortPayJson, ShortPayJson, ShortPayPatternJson, ShortPayStatus } from "@/api/accounting.tsx"
import { differenceInCalendarDays, format, parseISO } from "date-fns"

export type SortDir = "asc" | "desc"

/** A sortable cell value: strings compare case-insensitively, numbers numerically, missing last. */
export type SortValue = string | number | undefined

/**
 * Compare two cell values for column sorting. Missing values always sink to the bottom regardless
 * of direction, so flipping a column never surfaces a wall of "—" rows first.
 */
export function compare_sort_values(a: SortValue, b: SortValue, dir: SortDir): number {
  if (a === undefined && b === undefined) {
    return 0
  }
  if (a === undefined) {
    return 1
  }
  if (b === undefined) {
    return -1
  }
  let cmp: number
  if (typeof a === "number" && typeof b === "number") {
    cmp = a - b
  } else {
    cmp = String(a).toLowerCase().localeCompare(String(b).toLowerCase(), undefined, { numeric: true })
  }
  return dir === "asc" ? cmp : -cmp
}

/** Stable sort of table rows by one column, leaving the input untouched. */
export function sort_rows<T>(rows: T[], dir: SortDir, value: (row: T) => SortValue): T[] {
  return rows
    .map((row: T, index: number) => ({ row, index }))
    .sort((a: { row: T; index: number }, b: { row: T; index: number }) => {
      const cmp = compare_sort_values(value(a.row), value(b.row), dir)
      return cmp !== 0 ? cmp : a.index - b.index
    })
    .map((entry: { row: T; index: number }) => entry.row)
}

/**
 * Mirror of the server's `percent_bucket`: the nearest half point, so near-identical deal terms
 * group (11.87% / 12.08% / 12.10% are one 12% pattern) while different rates stay apart.
 */
export function short_pay_percent_bucket(percent: number): number {
  return Math.round(percent * 2)
}

/**
 * Whether a detected row belongs to a pattern warning — same customer, same shortfall-percent
 * bucket the server grouped on. Lets a pattern click filter the detected table to its invoices.
 */
export function pattern_matches_row(pattern: ShortPayPatternJson, row: DetectedShortPayJson): boolean {
  const total = Number(row.invoice_total)
  if (total <= 0 || row.customer_name !== pattern.customer_name) {
    return false
  }
  const row_percent = (100 * Number(row.amount)) / total
  return short_pay_percent_bucket(row_percent) === short_pay_percent_bucket(pattern.percent)
}

export function status_badge_class(status: ShortPayStatus): string {
  switch (status) {
    case "open":
      return "bg-amber-100 text-amber-800"
    case "resolved":
      return "bg-green-100 text-green-800"
    case "ignored":
      return "bg-slate-100 text-slate-600"
  }
}

export function age_days(created_at: string): number {
  try {
    return Math.max(0, differenceInCalendarDays(new Date(), parseISO(created_at)))
  } catch {
    return 0
  }
}

export function format_date(date?: string): string {
  if (!date) {
    return "—"
  }
  try {
    return format(parseISO(date), "MM/dd/yyyy")
  } catch {
    return date
  }
}

/** Sum dollar amounts through cents so a long list doesn't accumulate float error. */
export function sum_amounts(rows: { amount: number }[]): number {
  return rows.reduce((sum: number, row: { amount: number }) => sum + Math.round(Number(row.amount) * 100), 0) / 100
}

/** The collectable slice of a detected list: gaps the accounting system still shows outstanding. */
export function sum_outstanding(rows: DetectedShortPayJson[]): number {
  return rows.reduce((sum: number, row: DetectedShortPayJson) => sum + Math.round(Number(row.outstanding) * 100), 0) / 100
}

export function open_short_pays(rows: ShortPayJson[]): ShortPayJson[] {
  return rows.filter((row: ShortPayJson) => row.status === "open")
}

/**
 * What the customer is on the hook for across both populations: open recorded short pays plus every
 * detected one (detected rows are un-acted-on by definition, and the server already drops any whose
 * invoice has a recorded row).
 */
export function total_exposure(recorded: ShortPayJson[], detected: DetectedShortPayJson[]): number {
  return sum_amounts([...open_short_pays(recorded), ...detected])
}
